import { defineMessages, type MessageDescriptor } from 'react-intl';

export type DeviceButtonArtPosition = {
  /** Percent rect over the model's device SVG body. */
  x: number;
  y: number;
  w: number;
  h: number;
};

export type DeviceButtonCatalogEntry = {
  id: string;
  label: MessageDescriptor;
  /**
   * Marks a key drawn on the device SVG art; its outline comes from DEVICE_KEY_SHAPES.
   * Entries without a position have no key on the art yet.
   */
  artPosition?: DeviceButtonArtPosition;
};

export type DeviceButtonCatalog = DeviceButtonCatalogEntry[];

export const deviceButtonLabelMessages = defineMessages({
  record: {
    defaultMessage: 'Record',
    id: 'dFpm1pM9Qt',
    description:
      'Draft label pending copy review and hardware validation; do not treat as final.',
  },
  rec: {
    defaultMessage: 'Rec',
    id: '2I12xxubN0',
    description:
      'Draft label pending copy review and hardware validation; do not treat as final.',
  },
  play: {
    defaultMessage: 'Play',
    id: 'fcPTZ+eN9q',
    description:
      'Draft label pending copy review and hardware validation; do not treat as final.',
  },
  ff: {
    defaultMessage: 'Fast forward',
    id: 'GAZrRB4Xk8',
    description: 'Dictaphone fast-forward button.',
  },
  rew: {
    defaultMessage: 'Rewind',
    id: 'FPGup1UOG3',
    description: 'Dictaphone rewind button.',
  },
  eol: {
    defaultMessage: 'EOL',
    id: 'HKNWS2U4E3',
    description:
      'Draft label pending copy review and hardware validation; do not treat as final.',
  },
  ins: {
    defaultMessage: 'INS',
    id: 'RPyRWgSgzs',
    description:
      'Draft label pending copy review and hardware validation; do not treat as final.',
  },
  insert: {
    defaultMessage: 'Insert',
    id: 'DUBonZcdkn',
    description:
      'Draft label pending copy review and hardware validation; do not treat as final.',
  },
  new: {
    defaultMessage: 'NEW',
    id: 'PKZyvzad2l',
    description:
      'Draft label pending copy review and hardware validation; do not treat as final.',
  },
  f1: {
    defaultMessage: 'F1',
    id: 'GW1nw4lHja',
    description:
      'Draft label pending copy review and hardware validation; do not treat as final.',
  },
  f2: {
    defaultMessage: 'F2',
    id: 'h/DHCG9f6S',
    description:
      'Draft label pending copy review and hardware validation; do not treat as final.',
  },
  f3: {
    defaultMessage: 'F3',
    id: 'V8t2WouaM0',
    description:
      'Draft label pending copy review and hardware validation; do not treat as final.',
  },
  f4: {
    defaultMessage: 'F4',
    id: 'qKhsT0Aem1',
    description:
      'Draft label pending copy review and hardware validation; do not treat as final.',
  },
  trackballPress: {
    defaultMessage: 'Trackball',
    id: '9nfNU/Ah5T',
    description:
      'Draft label pending copy review and hardware validation; do not treat as final.',
  },
  info: {
    defaultMessage: 'Info',
    id: 'oIDvZ9g9T2',
    description: 'RecMic information button.',
  },
  tabForward: {
    defaultMessage: 'Tab forward',
    id: 'lFGMh9Iy2t',
    description:
      'Draft label pending copy review and hardware validation; do not treat as final.',
  },
  tabBackward: {
    defaultMessage: 'Tab backward',
    id: 'W2W30C94J0',
    description:
      'Draft label pending copy review and hardware validation; do not treat as final.',
  },
  enter: {
    defaultMessage: 'Enter',
    id: '7huJi8z2tK',
    description:
      'Draft label pending copy review and hardware validation; do not treat as final.',
  },
  stop: {
    defaultMessage: 'Stop',
    id: 'HxljeJ/drE',
    description:
      'Draft label pending copy review and hardware validation; do not treat as final.',
  },
  a: {
    defaultMessage: 'A',
    id: 'gFhqDLa2ya',
    description:
      'Draft label pending copy review and hardware validation; do not treat as final.',
  },
  b: {
    defaultMessage: 'B',
    id: 'GFp/aPtDJ8',
    description:
      'Draft label pending copy review and hardware validation; do not treat as final.',
  },
  c: {
    defaultMessage: 'C',
    id: '2soxUWpwf4',
    description:
      'Draft label pending copy review and hardware validation; do not treat as final.',
  },
  d: {
    defaultMessage: 'D',
    id: '3tXK6kxXMY',
    description:
      'Draft label pending copy review and hardware validation; do not treat as final.',
  },
  command: {
    defaultMessage: 'Instruction',
    id: '26m2kSi1Qp',
    description: 'SpeechMike Instruction / command button.',
  },
  instr: {
    defaultMessage: 'Instr',
    id: 'h1T5xXDnJS',
    description: 'SpeechMike instruction event distinct from the command button.',
  },
  scanEnd: {
    defaultMessage: 'Scan',
    id: 'TDdKbzEAZa',
    description: 'SpeechMike barcode scan-end button.',
  },
  scanSuccess: {
    defaultMessage: 'Scan OK',
    id: '0dfUfB6Srj',
    description: 'SpeechMike barcode scan-success button.',
  },
  transcribe: {
    defaultMessage: 'Transcribe',
    id: 'TCNADpbRD2',
    description: 'PowerMic III transcribe button.',
  },
  customLeft: {
    defaultMessage: 'Left',
    id: 'r200GcGzFN',
    description: 'PowerMic III left programmable button.',
  },
  customRight: {
    defaultMessage: 'Right',
    id: 'gYUGwBbEAa',
    description: 'PowerMic III right programmable button.',
  },
});

const button = (
  id: string,
  label: MessageDescriptor,
  artPosition?: DeviceButtonArtPosition
): DeviceButtonCatalogEntry => ({ id, label, artPosition });

const POWER_MIC_3: DeviceButtonCatalog = [
  button('transcribe', deviceButtonLabelMessages.transcribe),
  button('record', deviceButtonLabelMessages.record, {
    x: 27.3,
    y: 35.3,
    w: 45.3,
    h: 6.7,
  }),
  button('tab_backward', deviceButtonLabelMessages.tabBackward, {
    x: 8.3,
    y: 29.6,
    w: 31,
    h: 4.3,
  }),
  button('tab_forward', deviceButtonLabelMessages.tabForward, {
    x: 60.7,
    y: 29.6,
    w: 31,
    h: 4.3,
  }),
  button('rew', deviceButtonLabelMessages.rew, { x: 8.3, y: 35.3, w: 15.3, h: 14.5 }),
  button('ff', deviceButtonLabelMessages.ff, { x: 76.3, y: 35.3, w: 15.3, h: 14.5 }),
  button('play', deviceButtonLabelMessages.play, { x: 27.3, y: 43, w: 45.3, h: 6.7 }),
  button('enter', deviceButtonLabelMessages.enter),
  button('custom_left', deviceButtonLabelMessages.customLeft, {
    x: 8.3,
    y: 65.4,
    w: 20.7,
    h: 3.3,
  }),
  button('custom_right', deviceButtonLabelMessages.customRight, {
    x: 71,
    y: 65.4,
    w: 20.7,
    h: 3.3,
  }),
];

/** PowerMic 4 vendor-HID names. Not the SpeechMike map — same report, different bits. */
const POWER_MIC_4: DeviceButtonCatalog = [
  button('record', deviceButtonLabelMessages.record, {
    x: 27.3,
    y: 35.3,
    w: 45.3,
    h: 6.7,
  }),
  button('tab_backward', deviceButtonLabelMessages.tabBackward, {
    x: 8.3,
    y: 29.6,
    w: 31,
    h: 4.3,
  }),
  button('tab_forward', deviceButtonLabelMessages.tabForward, {
    x: 60.7,
    y: 29.6,
    w: 31,
    h: 4.3,
  }),
  button('rew', deviceButtonLabelMessages.rew, { x: 8.3, y: 35.3, w: 15.3, h: 14.5 }),
  button('ff', deviceButtonLabelMessages.ff, { x: 76.3, y: 35.3, w: 15.3, h: 14.5 }),
  button('play', deviceButtonLabelMessages.play, { x: 27.3, y: 43, w: 45.3, h: 6.7 }),
  button('enter', deviceButtonLabelMessages.enter),
  button('command', deviceButtonLabelMessages.command),
  button('f1', deviceButtonLabelMessages.f1, { x: 8.3, y: 65.4, w: 20.7, h: 3.3 }),
  button('f2', deviceButtonLabelMessages.f2, { x: 8.3, y: 70.7, w: 20.7, h: 3.3 }),
  button('f3', deviceButtonLabelMessages.f3, { x: 71, y: 65.4, w: 20.7, h: 3.3 }),
  button('f4', deviceButtonLabelMessages.f4, { x: 71, y: 70.7, w: 20.7, h: 3.3 }),
];

/** Philips SpeechMike Premium. Ids match the published vendor-HID bit map. */
const SPEECH_MIKE: DeviceButtonCatalog = [
  // EoL, info and Ins share one row. EoL and Ins are the wide keys; info sits
  // in the gap. EoL's left edge lines up with rewind / F1.
  button('eol', deviceButtonLabelMessages.eol, { x: 8, y: 26.1, w: 32, h: 4.1 }),
  // Upper half of the centre rocker, the full well around the red dot — not
  // the dot itself, and not the top EoL / info / Ins row.
  button('record', deviceButtonLabelMessages.record, {
    x: 30,
    y: 31.2,
    w: 38.7,
    h: 6.3,
  }),
  button('ins', deviceButtonLabelMessages.ins, { x: 60, y: 26.1, w: 32, h: 4.1 }),
  button('rew', deviceButtonLabelMessages.rew, { x: 8, y: 31.2, w: 19, h: 13.2 }),
  // Lower half of the same rocker, the full well around play / pause.
  button('play', deviceButtonLabelMessages.play, {
    x: 30,
    y: 37.8,
    w: 38.7,
    h: 6.6,
  }),
  // Fast-forward matches rewind: same top, same height, right column.
  button('ff', deviceButtonLabelMessages.ff, { x: 73, y: 31.2, w: 19, h: 13.2 }),
  button('stop', deviceButtonLabelMessages.stop),
  button('command', deviceButtonLabelMessages.command),
  // The (i) key between EoL and Ins reports as `instr`.
  button('instr', deviceButtonLabelMessages.instr, {
    x: 41.7,
    y: 26.1,
    w: 16.7,
    h: 4.1,
  }),
  button('scan_end', deviceButtonLabelMessages.scanEnd),
  button('scan_success', deviceButtonLabelMessages.scanSuccess),
  button('f1', deviceButtonLabelMessages.f1, { x: 8, y: 56.5, w: 20.3, h: 3.2 }),
  button('f2', deviceButtonLabelMessages.f2, { x: 8, y: 61.2, w: 20.3, h: 3.1 }),
  // F3 / F4 sit on the fast-forward column, not inset toward the centre.
  button('f3', deviceButtonLabelMessages.f3, { x: 72, y: 56.5, w: 20.3, h: 3.2 }),
  button('f4', deviceButtonLabelMessages.f4, { x: 72, y: 61.2, w: 20.3, h: 3.1 }),
];

/** Olympus RecMic II. Ids are live presses. Trackball and mouse clicks are not listed. */
const REC_MIC: DeviceButtonCatalog = [
  button('new', deviceButtonLabelMessages.new, { x: 10.3, y: 20.6, w: 22.7, h: 6.5 }),
  button('info', deviceButtonLabelMessages.info),
  button('rew', deviceButtonLabelMessages.rew, { x: 10.3, y: 28.2, w: 22.7, h: 21.8 }),
  button('ff', deviceButtonLabelMessages.ff, { x: 67, y: 28.2, w: 22.7, h: 21.8 }),
  // The slide switch in its top (REC) position: red dot and grip, above the
  // round `b` key — not the slide's lower travel.
  button('record', deviceButtonLabelMessages.rec, {
    x: 35,
    y: 18.4,
    w: 30,
    h: 20.9,
  }),
  // The small F keys below `a` / `c` — F1 / F2 on the left, F3 / F4 on the
  // right, as labelled on the mock.
  button('f1', deviceButtonLabelMessages.f1, { x: 10.3, y: 60.7, w: 22.7, h: 3.4 }),
  button('f2', deviceButtonLabelMessages.f2, { x: 10.3, y: 65.8, w: 22.7, h: 3.4 }),
  button('f3', deviceButtonLabelMessages.f3, { x: 67, y: 60.7, w: 22.7, h: 3.4 }),
  button('f4', deviceButtonLabelMessages.f4, { x: 67, y: 65.8, w: 22.7, h: 3.4 }),
];

/** Trackball and mouse clicks are not mappable on any device. */
const NON_ASSIGNABLE_BUTTON_IDS = new Set([
  'trackball',
  'trackball_press',
  'left_click',
  'right_click',
  'middle_click',
]);

export const isAssignableDeviceButton = (buttonId: string): boolean =>
  !NON_ASSIGNABLE_BUTTON_IDS.has(buttonId);

export const DEVICE_BUTTON_CATALOGS: Record<string, DeviceButtonCatalog> = {
  speech_mike: SPEECH_MIKE,
  power_mic_4: POWER_MIC_4,
  power_mic_3: POWER_MIC_3,
  rec_mic: REC_MIC,
};

export const getDeviceButtonCatalog = (model: string): DeviceButtonCatalog =>
  DEVICE_BUTTON_CATALOGS[model] ?? SPEECH_MIKE;
